import React, { Component, useCallback } from "react";
import { LogFirebase } from "../../services/firestore";

/**
 * Error Boundary component for Ask AI feature
 * Catches JavaScript errors in child components and displays fallback UI
 * Issue #4: Missing Error Boundary - Improves app stability
 *
 * Note: Error Boundaries must be class components in React 18 as there's no
 * hook equivalent for componentDidCatch/getDerivedStateFromError.
 * This implementation uses a class internally with a functional wrapper pattern.
 */

// Fallback UI Component (Functional)
const ErrorFallbackUI = ({ onRetry }) => {
    const handleRetryClick = useCallback(() => {
        onRetry?.();
    }, [onRetry]);

    return (
        <div className='askai-error-boundary'>
            <div className='askai-error-content'>
                <span className='askai-error-icon icon-warning'></span>
                <h4 className='askai-error-title'>Something went wrong</h4>
                <p className='askai-error-message'>The AI assistant encountered an error. Please try again.</p>
                <button className='btn btn-primary askai-error-retry-btn' onClick={handleRetryClick} type='button'>
                    <span className='btn-text'>Try Again</span>
                </button>
            </div>
        </div>
    );
};

// Error Boundary Class (Required for error catching in React 18)
class ErrorBoundaryClass extends Component {
    state = {
        hasError: false,
        error: null,
    };

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        // Log error to Firebase for monitoring
        LogFirebase("AskAI", "ErrorBoundary_caught", {
            error: error?.message || "Unknown error",
            componentStack: errorInfo?.componentStack?.substring(0, 500) || "",
        });

        // Log to console in development
        if (process.env.NODE_ENV === "development") {
            console.error("AskAI Error Boundary caught an error:", error, errorInfo);
        }
    }

    resetError = () => {
        this.setState({ hasError: false, error: null });
    };

    render() {
        const { hasError } = this.state;
        const { children, onRetry, FallbackComponent } = this.props;

        if (hasError) {
            const handleRetry = () => {
                this.resetError();
                onRetry?.();
            };

            if (FallbackComponent) {
                return <FallbackComponent onRetry={handleRetry} error={this.state.error} />;
            }

            return <ErrorFallbackUI onRetry={handleRetry} />;
        }

        return children;
    }
}

/**
 * AskAIErrorBoundary - Wrapper component exposing a clean API
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components to wrap
 * @param {Function} props.onRetry - Optional callback when user clicks retry
 * @param {React.ComponentType} props.FallbackComponent - Optional custom fallback component
 */
const AskAIErrorBoundary = ({ children, onRetry, FallbackComponent }) => {
    return (
        <ErrorBoundaryClass onRetry={onRetry} FallbackComponent={FallbackComponent}>
            {children}
        </ErrorBoundaryClass>
    );
};

export default AskAIErrorBoundary;
